import { useState, useEffect } from "react";
import "./RequirementsAcknowledgedSection.scss";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { getGcRequirementsView } from "services/projectService";
import { formatDate } from "utils/Common/Date";

interface Requirement {
  id: string;
  category: string;
  categoryOrder: number;
  itemOrder: number;
  title: string;
  description: string;
  type: string;
  configKey: string | null;
  value: string | null;
  requiredBy?: string[];
  isAccepted: "yes" | "no" | null;
  createdAt: string;
  updatedAt: string;
}

interface Ack {
  id: string;
  fullName: string;
  designation: string;
  companyName: string;
  gcSignatureUrl: string;
  isPublished: boolean;
  signedAt: string;
}

interface ViewData {
  requirements: Requirement[];
  ack: Ack | null;
  isSubmitted: boolean;
}

interface Props {
  projectId: string;
}

export default function RequirementsAcknowledgedSection({ projectId }: Props) {
  const [data, setData] = useState<ViewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    (getGcRequirementsView(projectId) as Promise<{ data: any; error: any }>).then(
      ({ data: res, error }) => {
        if (!error && res?.data) setData(res.data);
        setLoading(false);
      },
    );
  }, [projectId]);

  if (loading) {
    return (
      <div className="ds active" id="sec-requirements-acknowledged">
        <Skeleton width={260} height={18} style={{ marginBottom: 8 }} />
        <Skeleton width={400} height={13} style={{ marginBottom: 24 }} />
        {[1, 2, 3].map((n) => (
          <div className="card" key={n}>
            <Skeleton width={180} height={14} style={{ marginBottom: 14 }} />
            {[1, 2, 3].map((m) => (
              <Skeleton key={m} height={52} borderRadius={6} style={{ marginBottom: 8 }} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ds active" id="sec-requirements-acknowledged">
        <div className="da-sub" style={{ padding: "32px 0", textAlign: "center" }}>
          No requirements data available.
        </div>
      </div>
    );
  }

  // Group by category preserving categoryOrder sort
  const categoryMap = new Map<string, { order: number; items: Requirement[] }>();
  for (const req of data.requirements) {
    if (!categoryMap.has(req.category)) {
      categoryMap.set(req.category, { order: req.categoryOrder, items: [] });
    }
    categoryMap.get(req.category)!.items.push(req);
  }
  const categories = [...categoryMap.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([name, { items }]) => ({
      name,
      items: [...items].sort((a, b) => a.itemOrder - b.itemOrder),
    }));

  const { ack, isSubmitted } = data;

  return (
    <div className="ds active" id="sec-requirements-acknowledged">
      <div className="dsHeaderTitle">Requirements Acknowledged</div>
      <p className="ro-notice" style={{ marginBottom: 20 }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="var(--tx3)" strokeWidth="1.2" />
          <line x1="7" y1="5.5" x2="7" y2="9" stroke="var(--tx3)" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="7" cy="4" r=".6" fill="var(--tx3)" />
        </svg>
        These are the project requirements acknowledged by the GC before construction begins.
      </p>

      {isSubmitted && ack && (
        <div className="reqAckBanner">
          <span className="reqAckBannerIcon">✓</span>
          <div>
            <strong>Acknowledged &amp; Signed</strong>
            <span>
              {ack.fullName} · {ack.companyName} · {formatDate(ack.signedAt, false)}
            </span>
          </div>
        </div>
      )}

      {categories.map((cat) => (
        <div className="card" key={cat.name} style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 14 }}>
            {cat.name}
          </div>
          <div className="reqItemList">
            {cat.items.map((item) => (
              <div className="reqItem" key={item.id}>
                <div className={`reqCheckbox ${item.isAccepted === "yes" ? "reqChecked" : ""}`}>
                  {item.isAccepted === "yes" ? "✓" : ""}
                </div>
                <div className="reqItemBody">
                  <div className="reqItemHeader">
                    <span className="reqItemTitle">{item.title}</span>
                    {item.value && (
                      <span className="reqValueBadge">{item.value}</span>
                    )}
                  </div>
                  <p className="reqItemDesc">{item.description}</p>
                  {item.requiredBy && item.requiredBy.length > 0 && (
                    <div className="reqByList">
                      <span className="reqByLabel">Required by:</span>
                      {item.requiredBy.map((rb) => (
                        <span className="reqByChip" key={rb}>{rb}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {ack && (
        <div className="card reqAckCard">
          <div className="card-title">GC Acknowledgement</div>
          <div className="reqAckBody">
            <div className="reqAckInfo">
              <div className="reqAckName">{ack.fullName}</div>
              <div className="reqAckMeta">
                {ack.designation} · {ack.companyName}
              </div>
              {ack.signedAt && (
                <div className="reqAckDate">
                  Signed on {formatDate(ack.signedAt, false)}
                </div>
              )}
            </div>
            {ack.gcSignatureUrl && (
              <div className="reqSigWrap">
                <div className="reqSigLabel">Signature</div>
                <img
                  src={ack.gcSignatureUrl}
                  alt="GC Signature"
                  className="reqSigImg"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
